import type { Editor } from '@tiptap/core';

// Create an object with all editor commands
export const editorCommands = {
  // Headings
  toggleHeading1: (editor: Editor) => {
    return editor.chain().focus().toggleHeading({ level: 1 }).run();
  },

  toggleHeading2: (editor: Editor) => {
    return editor.chain().focus().toggleHeading({ level: 2 }).run();
  },

  toggleHeading3: (editor: Editor) => {
    return editor.chain().focus().toggleHeading({ level: 3 }).run();
  },

  // Lists
  toggleBulletList: (editor: Editor) => {
    return editor.chain().focus().toggleBulletList().run();
  },

  toggleOrderedList: (editor: Editor) => {
    return editor.chain().focus().toggleOrderedList().run();
  },

  toggleTaskList: (editor: Editor) => {
    const result = editor.chain().focus().toggleTaskList().run();

    // Ensure the editor maintains focus, but use a safer approach
    // that doesn't require direct DOM manipulation
    editor.commands.focus();

    return result;
  },

  // Block elements
  toggleBlockquote: (editor: Editor) => {
    return editor.chain().focus().toggleBlockquote().run();
  },

  toggleCodeBlock: (editor: Editor) => {
    return editor.chain().focus().toggleCodeBlock().run();
  },

  setHorizontalRule: (editor: Editor) => {
    return editor.chain().focus().setHorizontalRule().run();
  },

  // Inline formatting
  toggleBold: (editor: Editor) => {
    return editor.chain().focus().toggleBold().run();
  },

  toggleItalic: (editor: Editor) => {
    return editor.chain().focus().toggleItalic().run();
  },

  // Note: toggleUnderline is not a standard command in TipTap's StarterKit
  // You need to add the Underline extension for this to work

  toggleCode: (editor: Editor) => {
    return editor.chain().focus().toggleCode().run();
  },

  toggleHighlight: (editor: Editor) => {
    return editor.chain().focus().toggleHighlight().run();
  },

  // Table commands
  insertTable: (editor: Editor, rows = 3, cols = 3, withHeaderRow = true) => {
    // Create a table directly using the editor command
    if (
      editor &&
      editor.commands &&
      typeof editor.commands.insertTable === 'function'
    ) {
      return editor.commands.insertTable({ rows, cols, withHeaderRow });
    } else {
      console.error(
        'insertTable command not available. Check if Table extension is properly configured'
      );
      return false;
    }
  },

  addColumnBefore: (editor: Editor) => {
    return editor.chain().focus().addColumnBefore().run();
  },

  addColumnAfter: (editor: Editor) => {
    return editor.chain().focus().addColumnAfter().run();
  },

  deleteColumn: (editor: Editor) => {
    return editor.chain().focus().deleteColumn().run();
  },

  addRowBefore: (editor: Editor) => {
    return editor.chain().focus().addRowBefore().run();
  },

  addRowAfter: (editor: Editor) => {
    return editor.chain().focus().addRowAfter().run();
  },

  deleteRow: (editor: Editor) => {
    return editor.chain().focus().deleteRow().run();
  },

  deleteTable: (editor: Editor) => {
    return editor.chain().focus().deleteTable().run();
  },

  mergeCells: (editor: Editor) => {
    return editor.chain().focus().mergeCells().run();
  },

  splitCell: (editor: Editor) => {
    return editor.chain().focus().splitCell().run();
  },

  toggleHeaderCell: (editor: Editor) => {
    return editor.chain().focus().toggleHeaderCell().run();
  },

  // Undo/Redo
  undo: (editor: Editor) => {
    return editor.chain().focus().undo().run();
  },

  redo: (editor: Editor) => {
    return editor.chain().focus().redo().run();
  },
};
